/**
 * Onde a casa toca MESMO o chão — a sombra de contacto do escritório 2D.
 *
 * A elipse genérica que assenta a mobília (0,84 da pegada, colada ao fundo da
 * arte) assume duas coisas que são verdade em toda a mobília e falsas em todas
 * as casas: que o prop assenta na LINHA DE BAIXO da sua arte e que a base ocupa
 * a LARGURA TODA dela. Numa casa vista em 3/4 não assenta nem ocupa — o pixel
 * mais baixo do ficheiro são os degraus de madeira e o arbusto da frente, e o
 * chassis com as rodas corre em diagonal muito acima disso, à esquerda do meio.
 *
 * Este ficheiro existe porque o defeito voltou TRÊS vezes pela mesma porta, e
 * das três foi o Kawã a encontrá-lo numa fotografia: a cúpula que «parecia estar
 * a voar», depois três casas com «sombras pequenas que não se encaixam ao
 * padrão», e por fim o contentor laranja — «a sombra do contentor ainda está
 * bugada». Das duas primeiras o erro estava na MEDIÇÃO; da terceira estava no
 * MODELO, e é essa a diferença que este cabeçalho tem de explicar.
 *
 * ---------------------------------------------------------------------------
 * O TECTO DA ELIPSE: porque é que a regra anterior não podia lá chegar
 *
 * A regra que aqui esteve derivava a elipse de três constantes, e a do meio era
 * `ACHATAMENTO` — o `h/w` com que a elipse se lê. O comentário dizia que era «a
 * inclinação da CÂMARA e não uma propriedade da casa», e isso é verdade para um
 * CÍRCULO pintado no chão: seja onde for, uma câmara fixa encurta-o sempre na
 * mesma proporção. Só que a planta destas casas não é um círculo, é um
 * RECTÂNGULO — e o escorço de um rectângulo no ecrã depende também da razão da
 * planta e da rotação dela. Uma constante global não podia servir as duas
 * coisas, e a cúpula (essa sim redonda, 0,49) já tinha tido de fugir por uma
 * excepção. As de caixa ficaram com uma elipse rasa de mais, baixa de mais, e
 * que não chegava a passar por baixo do corpo.
 *
 * A aritmética fecha a porta a «afinar melhor a elipse», e fica escrita para
 * ninguém a repetir:
 *
 * - para a elipse CIRCUNSCREVER a planta da `homeModular` era preciso escalá-la
 *   1,415× — 251×72,5 px numa arte de 192. Uma poça, não uma sombra.
 * - para a INCLINAR até à recta L→R do chão (−7,8°) o semi-eixo menor tinha de
 *   ir a 45-48 px, o que empurra o fundo da elipse ~14 px abaixo da linha de
 *   baixo da arte, contra os ~3 aprovados em todos os props vizinhos. Duas
 *   iluminações diferentes na mesma relva.
 *
 * Cada variante de elipse troca um artefacto por outro. O modelo é que estava
 * errado.
 *
 * ---------------------------------------------------------------------------
 * A MEDIÇÃO: o perfil do pé dá a planta inteira
 *
 * A sombra de uma casa em caixa é a PLANTA dela, e a planta lê-se toda no perfil
 * do pé — a altura do píxel opaco mais baixo de cada coluna da arte. Nesse perfil
 * há três pontos que interessam:
 *
 *   N — o canto da FRENTE, o píxel mais baixo de toda a arte;
 *   L, R — os extremos das duas arestas que dele partem, andando para cada lado
 *          enquanto o pé SOBE devagar. Um salto grande num píxel só é o beiral
 *          do telhado a passar por cima de ar, e ar não toca no chão.
 *
 * O contorno de baixo do corpo SÃO as duas arestas da frente da planta. As duas
 * de trás são essas mesmas rodadas 180° em torno do meio de L-R, ou seja o
 * quarto canto é `F = L + R − N`. Quatro pontos, todos medidos, zero constantes
 * de gosto — e o ângulo do contacto deixa de ser um número à parte: é a própria
 * inclinação das arestas.
 *
 *   arte                      L          N          R          F
 *   home-modular-v1  192×116   2, 48     41,  0    187, 21    148, 69
 *   home-capsule-closed 160×96  0, 31    120,  0    159, 26     39, 57
 *
 * ---------------------------------------------------------------------------
 * A REGRA: duas constantes, e mais nada por casa
 *
 * A planta medida é o contacto EXACTO; uma sombra que fosse só isso não se via.
 * Cresce-se em torno do centro até assomar `REBORDO` px no canto mais lateral e
 * `FUNDO` px abaixo do canto da frente. Como se escala em torno do centro, os
 * cantos do meio crescem menos que os extremos — a sombra afina onde o corpo
 * encosta e engorda onde ele se afasta, que é o que a luz faz.
 *
 * Ficam de fora as EXCEPÇÕES, e cada uma tem de escrever porquê — é o que
 * impede que a próxima casa se torne excepção por distracção.
 */

import type { PropKind } from "@greenvoice/shared";

/** O que o desenho precisa de saber por casa que ainda usa ELIPSE. Px da ARTE. */
export type PeDeCasa = {
  /** Quanto a elipse sobe acima da linha de baixo da arte. */
  sobe: number;
  /** Largura da elipse em fracção da largura da arte. */
  larg: number;
  /** Altura da elipse em fracção do `TILE`. */
  alt: number;
  /** Desvio horizontal do centro da elipse, do meio da arte. */
  dx: number;
  /** Inclinação em graus (Phaser conta para baixo). */
  ang: number;
};

/**
 * Os três cantos do chão, medidos no PNG. Cada um é `[x na arte, altura em px
 * acima da linha de baixo da arte]` — o pé conta para CIMA, ao contrário do y.
 */
export type CantosDoPe = {
  /** Largura da arte em px — a mesma com que a casa é desenhada. */
  arte: number;
  /** Extremo da aresta que sai de `N` para a esquerda. */
  L: readonly [number, number];
  /** O canto da FRENTE: o píxel opaco mais baixo de toda a arte. */
  N: readonly [number, number];
  /** Extremo da aresta que sai de `N` para a direita. */
  R: readonly [number, number];
};

/**
 * A planta pronta a desenhar: pontos `[x, y]` a partir da origem do desenho —
 * x do meio da arte, y da linha de baixo dela e a crescer PARA BAIXO, como no
 * Phaser. É o que se passa tal e qual ao `scene.add.polygon`.
 */
export type PlantaDeCasa = readonly (readonly [number, number])[];

/** Px que a sombra assoma para além do canto mais lateral. É o que se vê. */
export const REBORDO = 10;

/** Px a que a sombra desce abaixo do canto da frente, o mais baixo de todos. */
export const FUNDO = 3;

/** A elipse dos props que não são casas: pousa a direito, na largura da pegada. */
export const SOMBRA_GENERICA = { larg: 0.84, alt: 0.52 } as const;

const cent = (v: number) => Math.round(v * 100) / 100;

/**
 * A regra. Dá o polígono inteiro a partir dos três cantos — a casa nova não
 * escolhe números, escolhe-se-lhe o ficheiro e mede-se.
 *
 * O quarto canto vem da simetria central da planta (`F = L + R − N`) e o
 * crescimento é uma escala em torno do meio de L-R: `REBORDO` e `FUNDO` são
 * EXACTOS nos cantos extremos, por construção, e proporcionalmente menores nos
 * outros. Não há aqui nenhum número escolhido a olho.
 */
export function plantaDaCasa(c: CantosDoPe): PlantaDeCasa {
  const F = [c.L[0] + c.R[0] - c.N[0], c.L[1] + c.R[1] - c.N[1]] as const;
  const cantos = [c.L, c.N, c.R, F] as const;
  const cx = (c.L[0] + c.R[0]) / 2;
  const cy = (c.L[1] + c.R[1]) / 2;
  const meiaL = Math.max(...cantos.map(([x]) => Math.abs(x - cx)));
  const meiaA = Math.max(...cantos.map(([, y]) => Math.abs(y - cy)));
  const sx = 1 + REBORDO / meiaL;
  const sy = 1 + FUNDO / meiaA;
  const meio = (c.arte - 1) / 2;
  return cantos.map(([x, y]) => [cent(cx + (x - cx) * sx - meio), cent(-(cy + (y - cy) * sy))] as const);
}

/**
 * As casas em CAIXA, medidas nos PNG. A planta que o mundo desenha sai daqui
 * pela regra — não há um ponto escrito à mão para nenhuma delas, e o teste volta
 * a medir a arte a cada corrida: a arte é substituída de vez em quando, e um pé
 * diferente é uma sombra errada outra vez, calada.
 */
export const CANTOS_DO_PE: Partial<Record<PropKind, CantosDoPe>> = {
  homeCapsuleClosed: { arte: 160, L: [0, 31], N: [120, 0], R: [159, 26] },
  homeModular: { arte: 192, L: [2, 48], N: [41, 0], R: [187, 21] },
};

/**
 * As excepções ao polígono, cada uma com o seu porquê escrito. O teste exige o
 * texto: uma casa só sai do padrão por decisão, nunca por esquecimento.
 *
 * As seis do parque não passam pela regra por uma razão mecânica antes de
 * qualquer outra: só elas levam `setScale` para a pegada (`PARK_HOME_PROPS`),
 * portanto a largura no ecrã delas é a PEGADA e não a arte, e uma planta medida
 * em px de arte não lhes serve. Ficam com o que a primeira passagem lhes deu,
 * que é o que o Kawã já dá por bom.
 */
export const SOMBRA_EXCEPCAO: Partial<Record<PropKind, PeDeCasa & { porque: string }>> = {
  mobileHome: {
    sobe: 12,
    larg: 0.82,
    alt: 1.05,
    dx: -16,
    ang: -12,
    porque: "casa do parque: escalada para a pegada, a largura no ecrã não é a da arte",
  },
  mobileHomeFlip: {
    sobe: 12,
    larg: 0.82,
    alt: 1.05,
    dx: 16,
    ang: 12,
    porque: "espelho da `mobileHome` — `dx` e `ang` trocam de sinal, o resto é o mesmo recorte",
  },
  mobileHomeSide: {
    sobe: 6,
    larg: 1,
    alt: 0.75,
    dx: 0,
    ang: 0,
    porque:
      "casa do parque vista de LADO: assenta a largura toda (saia 288 de 288 px de arte) e não tem diagonal de contacto nenhuma. A 0,083 de achatamento é a mais rasa de todas — sai do padrão de propósito e é a única a quem ninguém apontou nada; a corrigir, corrige-se a ver a arte, não a tabela",
  },
  mobileHomeSideFlip: {
    sobe: 6,
    larg: 1,
    alt: 0.75,
    dx: 0,
    ang: 0,
    porque: "espelho da `mobileHomeSide`, e simétrica: `dx` e `ang` são zero dos dois lados",
  },
  mobileHomeArch: {
    sobe: 24,
    larg: 0.82,
    alt: 1.3,
    dx: -24,
    ang: -14,
    porque:
      "casa do parque: escalada para a pegada. Foi a referência de achatamento (0,226) da regra da elipse que aqui esteve — a regra caiu, esta ficou como está porque nunca ninguém lhe apontou nada",
  },
  mobileHomeArchFlip: {
    sobe: 24,
    larg: 0.82,
    alt: 1.3,
    dx: 24,
    ang: 14,
    porque: "espelho da `mobileHomeArch` — `dx` e `ang` trocam de sinal",
  },
  homeDome: {
    sobe: 25,
    larg: 1.02,
    alt: 2,
    dx: 1,
    ang: 0,
    porque:
      "não é elipse de CHÃO nem planta de caixa: a cúpula assenta num deck CIRCULAR de madeira visto quase de cima (126×62 px), e o que se vê é um crescente escuro agarrado ao bordo da frente, onde a madeira encosta à relva. É o único prop em que o escorço constante da câmara valia mesmo — daí o 0,49. Uma mancha rasa colada ao fundo ficava quase toda FORA do deck, aberta em leque, e uma mancha que não toca no corpo só se lê de uma maneira: sombra projectada, ou seja corpo no ar — que foi metade do «parece estar a voar». O Kawã já a viu e deu-a por boa",
  },
  homeCapsule: {
    sobe: 21,
    larg: 1.01,
    alt: 1.62,
    dx: 0,
    ang: 0,
    porque:
      "cápsula suspensa em PERNAS: não tem planta que se leia no pé. O perfil de contacto dela são só as duas pernas e os degraus (L 69, N 98, R 179 de uma arte de 224), portanto um polígono medido dava uma lasca de 110 px no meio de uma casa que ocupa 206. O que ali se vê pela frincha entre as pernas é a escuridão acumulada debaixo do ventre, que é uma sombra de CORPO SUSPENSO e não de contacto — e é precisamente o que ali devia estar. Fica com a elipse da regra antiga, que ninguém contestou",
  },
};

/**
 * A tabela do polígono: as casas de caixa, cada uma pela regra. Quem tem planta
 * não tem elipse, e o teste garante que ninguém aparece nas duas.
 */
export const PLANTA_CASA: Partial<Record<PropKind, PlantaDeCasa>> = (() => {
  const tabela: Partial<Record<PropKind, PlantaDeCasa>> = {};
  for (const [kind, cantos] of Object.entries(CANTOS_DO_PE)) {
    tabela[kind as PropKind] = plantaDaCasa(cantos);
  }
  return tabela;
})();

/** A tabela da elipse: só as excepções, sem o porquê, que é para os humanos. */
export const SOMBRA_CASA: Partial<Record<PropKind, PeDeCasa>> = (() => {
  const tabela: Partial<Record<PropKind, PeDeCasa>> = {};
  for (const [kind, excepcao] of Object.entries(SOMBRA_EXCEPCAO)) {
    const { porque: _porque, ...pe } = excepcao;
    tabela[kind as PropKind] = pe;
  }
  return tabela;
})();
